/**
 * Estado derivado de la traza — runtime, NO persistido.
 *
 * El sistema sigue el patrón ARCH-7 del mockup: la fuente única de verdad
 * es `operacion.anmat.estado`. El estado "trazado" de un movimiento o serie
 * se DERIVA en runtime de los datos relacionados — no se almacena un boolean
 * que pueda divergir.
 *
 * Esto soporta naturalmente:
 *  - Multi-GLN: una serie con varias transacciones a lo largo de su vida.
 *  - Cancelaciones que revierten transacciones ya informadas.
 *  - Reconstruir el estado vigente leyendo solo la operación fuente.
 *
 * Las funciones son puras y reciben los datos relacionados como parámetros —
 * los componentes pasan los arrays que ya tienen del store.
 */

import type {
  Serie,
  Movimiento,
  Operacion,
  EstadoTrazaMov,
  EstadoTrazaSerie,
} from '../types';

/**
 * Deriva el estado de traza de un movimiento mirando su operación asociada.
 *
 * Reglas:
 *   - `mov` null o vacío → `'pendiente'`
 *   - Sin `operacionId` (alta histórica del seed o alta manual sin despacho) → `'enviada'`
 *   - `tipoEvento` es de cancelación → `'cancelada'` (el evento mismo no es "pendiente")
 *   - Con operación: mapa desde `op.anmat.estado`
 */
export function derivarEstadoTrazaMov(
  mov: Movimiento | null | undefined,
  operacion: Operacion | null | undefined,
): EstadoTrazaMov {
  if (!mov) return 'pendiente';
  if (!mov.operacionId) return 'enviada'; // alta histórica del seed o alta manual
  if (mov.tipoEvento === 'cancelacion' || mov.tipoEvento === 'cancelacion_recepcion') {
    return 'cancelada';
  }
  if (!operacion || !operacion.anmat) return 'pendiente';
  switch (operacion.anmat.estado) {
    case 'no_informada':
    case 'en_cola':
      return 'pendiente';
    case 'informada':
    case 'confirmada':
      return 'enviada';
    case 'error':
      return 'error';
    case 'cancelada':
      return 'cancelada';
    default:
      return 'pendiente';
  }
}

/**
 * Deriva el estado de traza de una serie recorriendo sus movimientos y mirando
 * el estado de cada operación asociada.
 *
 * Estados devueltos:
 *   - `'trazada'`         → al menos una op está en `informada`/`confirmada`
 *   - `'pendiente'`       → tiene ops en `en_cola`/`no_informada`/`error`,
 *                            ninguna informada/confirmada
 *   - `'solo_canceladas'` → todas las ops fueron canceladas (sin traza vigente)
 *   - `'nunca_trazada'`   → alta manual sin despachos posteriores
 *
 * `serie.altaManual` (metadato inmutable del origen) se usa solo como fallback
 * para distinguir series del seed histórico (sin movs → se asume trazada) de
 * series creadas vía ingreso manual (sin movs con op → nunca_trazada).
 */
export function derivarEstadoTrazaSerie(
  serie: Serie | null | undefined,
  movimientos: readonly Movimiento[],
  operaciones: readonly Operacion[],
): EstadoTrazaSerie {
  if (!serie) return 'nunca_trazada';

  const movsDeSerie = movimientos.filter((m) => m.serieId === serie.id);

  // Index de operaciones por id para lookup O(1)
  const opsById = new Map(operaciones.map((o) => [o.id, o]));

  const opsEstados = movsDeSerie
    .map((m) => (m.operacionId ? opsById.get(m.operacionId) : null))
    .filter((op): op is Operacion => Boolean(op?.anmat))
    .map((op) => op.anmat.estado);

  if (opsEstados.some((e) => e === 'informada' || e === 'confirmada')) {
    return 'trazada';
  }
  if (opsEstados.some((e) => e === 'en_cola' || e === 'no_informada' || e === 'error')) {
    return 'pendiente';
  }
  if (opsEstados.length > 0) {
    // Hubo ops asociadas, todas están en 'cancelada'
    return 'solo_canceladas';
  }
  // Sin movimientos con operación: distinguir histórico del seed vs alta manual pura
  return serie.altaManual ? 'nunca_trazada' : 'trazada';
}
